import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { SanitizeHtml, Image, ApplyClassOnScroll, ApplyClassOnGroup } from '@bd/bdcore-utilities';
import { css } from '@bd/bdui-styles/emotion';
import ButtonSet from '../../ButtonSet';
import { colors, typography } from '../../../styles';

const styles = css`
  .HomeContent {
    overflow: hidden;
    padding: 30px 0;
    position: relative;
    background-color: ${colors.neutral};
    @media (min-width: 768px) {
      padding: 75px 0 150px 0;
    }
  }
  .HomeContent_content {
    position: relative;
    z-index: 2;
    padding: 0;
    margin: -45px 0 0 0;
    @media (min-width: 1200px) {
      padding: 90px 0 90px 0;
      margin: 0;
    }
    @media (min-width: 1600px) {
      padding: 220px 0 150px 0;
      margin: 0;
    }
  }
  .HomeContent_headlineWrapper {
    margin: 0;
  }
  .HomeContent_headline {
    color: ${colors.brand1};
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1;
    margin: 0 0 20px 0;
    text-align: center;
    display: block;
    @media (min-width: 768px) {
      text-align: left;
    }
  }
  .HomeContent_subheadline {
    font-weight: normal;
    display: block;
    font-size: 32px;
    font-family: ${typography.Serif};
    text-transform: none;
    color: ${colors.body};
    font-weight: 600;
    margin: 0 0 20px 0;
    line-height: 1;
    display: block;
    text-align: center;
    @media (min-width: 768px) {
      font-size: 48px;
      text-align: left;
    }
  }
  .HomeContent_body p {
    font-size: 21px;
    line-height: 1.5;
    font-weight: 400;
    font-family: ${typography.Condensed};
    margin: 0 10px;
    text-align: center;
    @media (min-width: 768px) {
      font-size: 24px;
      margin: 0;
      text-align: left;
      &::after {
        content: '';
        display: block;
        margin: 15px 0 45px 0;
        width: 150px;
        height: 2px;
        background-color: ${colors.brand3};
      }
    }
  }
  .HomeContent_iconsList {
    list-style: none;
    margin: 0 0 0 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    @media (min-width: 768px) {
      justify-content: flex-start;
      margin: 0 0 15px 0;
    }
  }
  .HomeContent_iconsItem {
    display: block;
    margin: 0 10px 20px 10px;
    @media (min-width: 768px) {
      margin: 0 20px 20px 0;
    }
    img {
      width: 70px;
      height: 70px;
      @media (min-width: 1200px) {
        width: 90px;
        height: 90px;
      }
    }
  }
  .HomeContent_media {
    position: relative;
    width: 100%;
    height: 100%;
    @media (min-width: 768px) {
      font-size: 24px;
      margin: 0;
      width: 100%;
      height: 100%;
    }
  }
  .HomeContent_mediaImage {
    position: relative;
    width: 100%;
    margin: 0 auto;
    @media (min-width: 768px) {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 110%;
      z-index: 1;
    }
  }
`;

function HomeContent({ content }) {
  return (
    <div className={styles}>
      <div className="HomeContent">
        <ApplyClassOnScroll
          className="container"
          once
          triggerGroup="pageSection"
          invisible="invisible"
          visible="animate__animated animate__fadeIn"
          offset={-300}
        >
          <div className="row">
            <ApplyClassOnGroup
              className="col-md-5 col-xl-7"
              group="pageSection"
              invisible="invisible"
              visible={{
                0: '',
                500: 'animate__animated animate__fadeInLeft',
              }}
            >
              <div className="HomeContent_media">
                <Image
                  className="HomeContent_mediaImage"
                  src={get(content, '[0].image.contentUrl')}
                  width={1200}
                  height={1200}
                  alt={get(content, '[0].title', get(content, '[0].image.caption'), '')}
                />
              </div>
            </ApplyClassOnGroup>
            <ApplyClassOnGroup
              className="col-md-7 col-xl-5"
              group="pageSection"
              invisible="invisible"
              visible={{
                0: '',
                500: 'animate__animated animate__fadeInRight',
              }}
            >
              <div className="HomeContent_content">
                <h1 className="HomeContent_headlineWrapper">
                  <span className="HomeContent_headline">{get(content, '[0].title')}</span>
                  <span className="HomeContent_subheadline">{get(content, '[0].post_title')}</span>
                </h1>
                <div className="HomeContent_body" dangerouslySetInnerHTML={SanitizeHtml(get(content, '[0].body'))} />
                <ul className="HomeContent_iconsList">
                  <li className="HomeContent_iconsItem"><img src={get(content, '[0].icon_1.contentUrl')} alt={get(content, '[0].icon_1.name')} title={get(content, '[0].icon_1.caption')} /></li>
                  <li className="HomeContent_iconsItem"><img src={get(content, '[0].icon_2.contentUrl')} alt={get(content, '[0].icon_2.name')} title={get(content, '[0].icon_2.caption')} /></li>
                  <li className="HomeContent_iconsItem"><img src={get(content, '[0].icon_3.contentUrl')} alt={get(content, '[0].icon_3.name')} title={get(content, '[0].icon_3.caption')} /></li>
                  <li className="HomeContent_iconsItem"><img src={get(content, '[0].icon_4.contentUrl')} alt={get(content, '[0].icon_4.name')} title={get(content, '[0].icon_4.caption')} /></li>
                </ul>
                <ButtonSet buttonSetParent={get(content, '[0]')} />
              </div>
            </ApplyClassOnGroup>
          </div>
        </ApplyClassOnScroll>
      </div>
    </div>
  );
}

HomeContent.propTypes = {
  content: PropTypes.arrayOf(PropTypes.object).isRequired,
};

HomeContent.defaultProps = {
};

export const sectionName = 'Home Conent';

export default HomeContent;



// WEBPACK FOOTER //
// ./src/components/PageLoader/Sections/HomeContent.section.js